<template>
  <div class="farm-list">
    <el-card>
      <div class="search-box">
        <el-row :gutter="20">
          <el-col :span="6">
            <el-input
              v-model="query.keyword"
              placeholder="请输入地块名称或作物名称"
              prefix-icon="el-icon-search"
              @keyup.enter.native="getList"
            ></el-input>
          </el-col>
          <el-col :span="5">
            <el-select v-model="query.status" placeholder="请选择状态">
              <el-option label="全部" :value="-1"></el-option>
              <el-option label="可认领" :value="0"></el-option>
              <el-option label="已认领" :value="1"></el-option>
              <el-option label="已收获" :value="2"></el-option>
            </el-select>
          </el-col>
          <el-col :span="5">
            <el-input
              v-model="query.crop_type"
              placeholder="请输入作物类型"
            ></el-input>
          </el-col>
        </el-row>
        <div class="search-buttons">
          <el-button type="primary" icon="el-icon-search" @click="getList">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetQuery">重置</el-button>
          <el-button type="success" icon="el-icon-plus" @click="openAddDialog">添加地块</el-button>
        </div>
      </div>

      <div class="table-container">
        <el-table :data="plotList" v-loading="loading" style="width: 100%">
          <el-table-column type="index" label="序号" width="60"></el-table-column>
          <el-table-column prop="name" label="地块名称" width="150"></el-table-column>
          <el-table-column prop="crop" label="种植作物" width="120"></el-table-column>
          <el-table-column prop="crop_type" label="作物类型" width="120"></el-table-column>
          <el-table-column prop="price" label="认领费用" width="100">
            <template slot-scope="scope">
            {{ scope.row.price }} 元
            </template>
          </el-table-column>
          <el-table-column prop="area" label="地块面积" width="100">
            <template slot-scope="scope">
            {{ scope.row.area }} ㎡
            </template>
          </el-table-column>
          <el-table-column prop="status_text" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag 
                :type="scope.row.status === 0 ? 'success' : scope.row.status === 1 ? 'warning' : 'info'" 
                size="small"
              >
                {{ scope.row.status_text }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="sort_order" label="排序" width="80"></el-table-column>
          <el-table-column prop="create_time_str" label="创建时间" width="180"></el-table-column>
          <el-table-column label="操作" width="340" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" @click="getDetail(scope.row)">详情</el-button>
              <el-button size="mini" type="primary" @click="openEditDialog(scope.row)">编辑</el-button>
              <el-button 
                size="mini" 
                type="success" 
                v-if="scope.row.status === 0" 
                @click="handleClaim(scope.row)"
              >
                认领
              </el-button>
              <el-button 
                size="mini" 
                type="warning" 
                v-if="scope.row.status === 1" 
                @click="handleHarvest(scope.row)"
              >
                收获
              </el-button>
              <el-button size="mini" type="info" v-if="scope.row.status !== 0" @click="handleReset(scope.row)">重置</el-button>
              <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <el-pagination
          class="pagination"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
          :current-page="query.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="query.limit"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        ></el-pagination>
      </div>
    </el-card>

    <el-dialog
      title="地块详情"
      :visible.sync="detailVisible"
      width="700px"
    >
      <div v-if="detailInfo" class="detail-content">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="地块名称" :span="2">{{ detailInfo.name }}</el-descriptions-item>
          <el-descriptions-item label="种植作物">{{ detailInfo.crop }}</el-descriptions-item>
          <el-descriptions-item label="作物类型">{{ detailInfo.crop_type }}</el-descriptions-item>
          <el-descriptions-item label="认领费用">{{ detailInfo.price }} 元</el-descriptions-item>
          <el-descriptions-item label="地块面积">{{ detailInfo.area }} ㎡</el-descriptions-item>
          <el-descriptions-item label="生长周期">{{ detailInfo.grow_days }} 天</el-descriptions-item>
          <el-descriptions-item label="地块位置">{{ detailInfo.location }}</el-descriptions-item>
          <el-descriptions-item label="排序">{{ detailInfo.sort_order }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag 
              :type="detailInfo.status === 0 ? 'success' : detailInfo.status === 1 ? 'warning' : 'info'" 
              size="small"
            >
              {{ detailInfo.status_text }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ detailInfo.create_time_str }}</el-descriptions-item>
          <el-descriptions-item label="更新时间">{{ detailInfo.update_time_str }}</el-descriptions-item>
        </el-descriptions>
        
        <div v-if="detailInfo.image" class="image-section">
          <h4 class="section-title">地块图片</h4>
          <img :src="detailInfo.image" class="plot-image" @click="previewImage(detailInfo.image)" />
        </div>

        <div v-if="detailInfo.description" class="description-section">
          <h4 class="section-title">地块描述</h4>
          <p>{{ detailInfo.description }}</p>
        </div>

        <div v-if="detailInfo.packages && detailInfo.packages.length > 0" class="packages-section">
          <h4 class="section-title">套餐</h4>
          <el-tag v-for="(pkg, idx) in detailInfo.packages" :key="idx" style="margin: 4px;">
            {{ pkg }}
          </el-tag>
        </div>

        <div v-if="detailInfo.benefits && detailInfo.benefits.length > 0" class="benefits-section">
          <h4 class="section-title">权益说明</h4>
          <ul class="benefits-list">
            <li v-for="(benefit, idx) in detailInfo.benefits" :key="idx">{{ benefit }}</li>
          </ul>
        </div>

        <div v-if="detailInfo.tags" class="tags-section">
          <h4 class="section-title">标签</h4>
          <el-tag v-for="(tag, idx) in detailInfo.tags.split(',')" :key="idx" style="margin: 4px;">
            {{ tag }}
          </el-tag>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="isEdit ? '编辑地块' : '添加地块'"
      :visible.sync="formVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form :model="formData" ref="formData" :rules="formRules" label-width="120px">
        <el-form-item label="地块名称" prop="name">
          <el-input v-model="formData.name" placeholder="请输入地块名称"></el-input>
        </el-form-item>
        <el-form-item label="种植作物" prop="crop">
          <el-input v-model="formData.crop" placeholder="请输入种植作物"></el-input>
        </el-form-item>
        <el-form-item label="作物类型" prop="crop_type">
          <el-input v-model="formData.crop_type" placeholder="请输入作物类型标识"></el-input>
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="生长周期" prop="grow_days">
              <el-input-number v-model="formData.grow_days" :min="1" :max="365" placeholder="天数" style="width: 100%;"></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="认领费用" prop="price">
              <el-input-number v-model="formData.price" :min="0" :precision="2" placeholder="元" style="width: 100%;"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="地块面积" prop="area">
              <el-input-number v-model="formData.area" :min="0" placeholder="㎡" style="width: 100%;"></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="排序权重" prop="sort_order">
              <el-input-number v-model="formData.sort_order" :min="0" placeholder="数字越小越靠前" style="width: 100%;"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="地块位置" prop="location">
          <el-input v-model="formData.location" placeholder="请输入地块位置"></el-input>
        </el-form-item>
        <el-form-item label="地块图片" prop="image">
          <el-upload
            :headers="token"
            accept=".jpg,.png,.jpeg,.gif"
            :on-remove="imgRemove"
            :limit="1"
            :action="imgUrl"
            list-type="picture"
            :on-exceed="exceed"
            :on-success="imgSuccess"
            name="image"
            :file-list="fileList"
          >
            <el-button size="small" type="primary">上传地块图片</el-button>
            <div slot="tip" class="el-upload__tip">只能上传jpg/png/jpeg/gif文件</div>
          </el-upload>
        </el-form-item>
        <el-form-item label="实时视频" prop="live_url">
          <el-upload
            :headers="token"
            accept="video/*"
            :on-remove="videoRemove"
            :limit="1"
            :action="videoUrl"
            list-type="video"
            :on-exceed="videoExceed"
            :on-success="videoSuccess"
            name="video"
            :file-list="videoList"
          >
            <el-button size="small" type="primary">上传视频</el-button>
            <div slot="tip" class="el-upload__tip">只能上传视频文件</div>
          </el-upload>
        </el-form-item>
        <el-form-item label="标签" prop="tags">
          <el-input v-model="formData.tags" placeholder="多个标签用逗号分隔"></el-input>
        </el-form-item>
        <el-form-item label="套餐" prop="packages">
          <div class="packages-container">
            <div v-for="(pkg, idx) in formData.packages" :key="idx" class="package-item">
              <div class="package-header">
                <span class="package-title">套餐 {{ idx + 1 }}</span>
                <el-button size="mini" type="danger" icon="el-icon-delete" @click="removePackage(idx)">删除</el-button>
              </div>
              <div class="package-form">
                <el-form-item label="套餐名称" :prop="'packages.' + idx + '.name'">
                  <el-input v-model="pkg.name" placeholder="套餐名称"></el-input>
                </el-form-item>
                <el-form-item label="价格(元)" :prop="'packages.' + idx + '.price'">
                  <el-input-number v-model="pkg.price" :min="0" :precision="2" style="width: 100%;"></el-input-number>
                </el-form-item>
                <el-form-item label="天数" :prop="'packages.' + idx + '.days'">
                  <el-input-number v-model="pkg.days" :min="0" style="width: 100%;"></el-input-number>
                </el-form-item>
                <el-form-item label="描述" :prop="'packages.' + idx + '.desc'">
                  <el-input v-model="pkg.desc" placeholder="描述"></el-input>
                </el-form-item>
              </div>
            </div>
            <el-button type="primary" icon="el-icon-plus" @click="addPackage">添加套餐</el-button>
          </div>
        </el-form-item>
        <el-form-item label="订购权益" prop="benefits">
          <div class="benefits-container">
            <div v-for="(benefit, idx) in formData.benefits" :key="idx" class="benefit-item">
              <el-input v-model="formData.benefits[idx]" placeholder="请输入权益说明" style="flex: 1; margin-right: 10px;"></el-input>
              <el-button size="small" type="danger" icon="el-icon-delete" @click="removeBenefit(idx)"></el-button>
            </div>
            <div class="add-benefit">
              <el-input v-model="newBenefit" placeholder="请输入新权益说明" style="width: 400px; margin-right: 10px;"></el-input>
              <el-button size="small" type="primary" icon="el-icon-plus" @click="addBenefit">添加权益</el-button>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="地块描述" prop="description">
          <el-input type="textarea" v-model="formData.description" placeholder="请输入地块描述" :rows="3"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="formVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>

    <el-image-viewer
      v-if="previewImageUrl"
      :url-list="[previewImageUrl]"
      :visible.sync="previewVisible"
    ></el-image-viewer>
  </div>
</template>

<script>
export default {
  name: 'LaobingFarmList',
  data() {
    return {
      loading: false,
      plotList: [],
      total: 0,
      query: {
        page: 1,
        limit: 20,
        status: -1,
        keyword: '',
        crop_type: ''
      },
      detailVisible: false,
      detailInfo: {},
      formVisible: false,
      isEdit: false,
      formData: {
        id: null,
        name: '',
        crop: '',
        crop_type: '',
        grow_days: 30,
        price: 0.00,
        area: 0,
        location: '',
        packages: [],
        benefits: [],
        tags: '',
        description: '',
        image: '',
        live_url: '',
        sort_order: 0
      },
      formRules: {
        name: [
          { required: true, message: '请输入地块名称', trigger: 'blur' }
        ],
        crop: [
          { required: true, message: '请输入种植作物', trigger: 'blur' }
        ],
        crop_type: [
          { required: true, message: '请输入作物类型', trigger: 'blur' }
        ]
      },
      previewImageUrl: '',
      previewVisible: false,
      fileList: [],
      videoList: [],
      newBenefit: '',
      token: {
        "Accept-Token": window.sessionStorage.getItem("token"),
      }
    }
  },
  computed: {
    imgUrl() {
      return 'https://erp.campgrounds.cn/admin/leifeng_farm/uploadImage';
    },
    videoUrl() {
      return 'https://erp.campgrounds.cn/admin/leifeng_farm/uploadVideo';
    },
  },
  created() {
    this.getList()
  },
  methods: {
    imgSuccess(response, file, fileList) {
      console.log(response);
      this.formData.image = response.data.url;
    },
    exceed() {
      this.$message({
        message: "数量超出",
        type: "warning",
      });
    },
    imgRemove() {
      this.formData.image = "";
      this.fileList = [];
    },
    videoSuccess(response, file, fileList) {
      console.log(response);
      this.formData.live_url = response.data.url;
    },
    videoExceed() {
      this.$message({
        message: "数量超出",
        type: "warning",
      });
    },
    videoRemove() {
      this.formData.live_url = "";
      this.videoList = [];
    },
    addPackage() {
      this.formData.packages.push({
        id: null,
        name: '',
        price: 0,
        days: 0,
        desc: ''
      });
    },
    removePackage(index) {
      this.formData.packages.splice(index, 1);
    },
    addBenefit() {
      if (!this.newBenefit.trim()) {
        this.$message.warning('请输入权益说明');
        return;
      }
      this.formData.benefits.push(this.newBenefit.trim());
      this.newBenefit = '';
    },
    removeBenefit(index) {
      this.formData.benefits.splice(index, 1);
    },
    async getList() {
      this.loading = true
      try {
        const params = {
          page: this.query.page,
          limit: this.query.limit,
          status: this.query.status,
          keyword: this.query.keyword,
          crop_type: this.query.crop_type
        }
        if (params.status === -1) delete params.status
        if (!params.keyword) delete params.keyword
        if (!params.crop_type) delete params.crop_type
        
        const { data: res } = await this.$axios.get('/admin/leifeng_farm/plotList', { params })
        if (res.code === 1) {
          const list = res.data.list || []
          // 给图片URL拼接域名
          list.forEach(item => {
            if (item.image && !item.image.startsWith('http')) {
              item.image = 'https://zhiqing.campgrounds.cn' + item.image
            }
          })
          this.plotList = list
          this.total = res.data.total || 0
        } else {
          this.$message.error(res.message || '获取地块列表失败')
        }
      } catch (error) {
        this.$message.error('网络错误，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    handleSizeChange(size) {
      this.query.limit = size
      this.getList()
    },
    handlePageChange(page) {
      this.query.page = page
      this.getList()
    },
    resetQuery() {
      this.query = {
        page: 1,
        limit: 20,
        status: -1,
        keyword: '',
        crop_type: ''
      }
      this.getList()
    },
    async getDetail(row) {
      try {
        const { data: res } = await this.$axios.get('/admin/leifeng_farm/plotDetail', {
          params: { id: row.id }
        })
        if (res.code === 1) {
          const detailInfo = res.data
          // 给图片URL拼接域名
          if (detailInfo.image && !detailInfo.image.startsWith('http')) {
            detailInfo.image = 'https://zhiqing.campgrounds.cn' + detailInfo.image
          }
          this.detailInfo = detailInfo
          this.detailVisible = true
        } else {
          this.$message.error(res.message || '获取详情失败')
        }
      } catch (error) {
        this.$message.error('网络错误，请稍后重试')
      }
    },
    openAddDialog() {
      this.isEdit = false;
      this.formData = {
        id: null,
        name: '',
        crop: '',
        crop_type: '',
        grow_days: 30,
        price: 0.00,
        area: 0,
        location: '',
        packages: [],
        benefits: [],
        tags: '',
        description: '',
        image: '',
        live_url: '',
        sort_order: 0
      };
      this.fileList = [];
      this.videoList = [];
      this.formVisible = true;
    },
    openEditDialog(row) {
      this.isEdit = true;
      // 编辑时去掉域名，只保留相对路径
      let imageUrl = row.image || '';
      if (imageUrl.startsWith('http')) {
        imageUrl = imageUrl.replace('https://zhiqing.campgrounds.cn', '');
      }
      // 处理 packages 数据，确保是数组格式
      let packages = row.packages || [];
      if (typeof packages === 'string') {
        try {
          packages = JSON.parse(packages);
        } catch (e) {
          packages = [];
        }
      }
      // 处理 benefits 数据，确保是数组格式
      let benefits = row.benefits || [];
      if (typeof benefits === 'string') {
        try {
          benefits = JSON.parse(benefits);
        } catch (e) {
          benefits = [];
        }
      }
      this.formData = {
        id: row.id,
        name: row.name,
        crop: row.crop,
        crop_type: row.crop_type,
        grow_days: row.grow_days || 30,
        price: row.price || 0.00,
        area: row.area || 0,
        location: row.location || '',
        packages: packages,
        benefits: benefits,
        tags: row.tags || '',
        description: row.description || '',
        image: imageUrl,
        live_url: row.live_url || '',
        sort_order: row.sort_order || 0
      };
      this.fileList = row.image ? [{ name: row.name, url: row.image }] : [];
      this.videoList = row.live_url ? [{ name: 'video', url: row.live_url }] : [];
      this.formVisible = true;
    },
    async submitForm() {
      this.$refs.formData.validate(async valid => {
        if (valid) {
          try {
            let url = this.isEdit 
              ? '/admin/leifeng_farm/plotEdit' 
              : '/admin/leifeng_farm/plotAdd'
            
            const data = {
              ...this.formData
            };
            // 提交时去掉域名，只保留相对路径
            if (data.image && data.image.startsWith('http')) {
              data.image = data.image.replace('https://zhiqing.campgrounds.cn', '');
            }
            if (!this.isEdit) delete data.id;
            
            const { data: res } = await this.$axios.post(url, data)
            if (res.code === 1) {
              this.$message.success(this.isEdit ? '编辑成功' : '添加成功')
              this.formVisible = false
              this.getList()
            } else {
              this.$message.error(res.message || '操作失败')
            }
          } catch (error) {
            this.$message.error('网络错误，请稍后重试')
          }
        }
      })
    },
    async handleDelete(row) {
      this.$confirm('确定要删除这个地块吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data: res } = await this.$axios.post('/admin/leifeng_farm/plotDelete', {
            id: row.id
          })
          if (res.code === 1) {
            this.$message.success('删除成功')
            this.getList()
          } else {
            this.$message.error(res.message || '删除失败')
          }
        } catch (error) {
          this.$message.error('网络错误，请稍后重试')
        }
      }).catch(() => {})
    },
    async handleClaim(row) {
      this.$confirm('确定要认领这个地块吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'success'
      }).then(async () => {
        try {
          const { data: res } = await this.$axios.post('/admin/leifeng_farm/plotClaim', {
            id: row.id
          })
          if (res.code === 1) {
            this.$message.success('认领成功')
            this.getList()
          } else {
            this.$message.error(res.message || '认领失败')
          }
        } catch (error) {
          this.$message.error('网络错误，请稍后重试')
        }
      }).catch(() => {})
    },
    async handleHarvest(row) {
      this.$confirm('确定要收获这个地块吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data: res } = await this.$axios.post('/admin/leifeng_farm/plotHarvest', {
            id: row.id
          })
          if (res.code === 1) {
            this.$message.success('收获成功')
            this.getList()
          } else {
            this.$message.error(res.message || '收获失败')
          }
        } catch (error) {
          this.$message.error('网络错误，请稍后重试')
        }
      }).catch(() => {})
    },
    async handleReset(row) {
      this.$confirm('确定要重置这个地块吗？重置后地块状态将变为可认领。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data: res } = await this.$axios.post('/admin/leifeng_farm/plotReset', {
            id: row.id
          })
          if (res.code === 1) {
            this.$message.success('重置成功')
            this.getList()
          } else {
            this.$message.error(res.message || '重置失败')
          }
        } catch (error) {
          this.$message.error('网络错误，请稍后重试')
        }
      }).catch(() => {})
    },
    previewImage(url) {
      this.previewImageUrl = url
      this.previewVisible = true
    }
  }
}
</script>

<style scoped>
.farm-list {
  padding: 20px 0;
}

.search-box {
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e4e7ed;
}

.search-buttons {
  margin-top: 15px;
}

.search-buttons .el-button {
  margin-right: 10px;
}

.table-container {
  margin-top: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: center;
}

.detail-content {
  padding: 10px 0;
}

.image-section,
.description-section,
.packages-section,
.benefits-section,
.tags-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 15px;
}

.plot-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #e4e7ed;
}

.benefits-list {
  margin: 0;
  padding-left: 20px;
}

.benefits-list li {
  margin-bottom: 8px;
}

.el-table {
  font-size: 13px;
}

.el-table__body-wrapper {
  overflow-x: auto;
}

.packages-container {
  width: 100%;
}

.package-item {
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background-color: #f5f7fa;
}

.package-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.package-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.package-form {
  padding-left: 10px;
}

.package-form .el-form-item {
  margin-bottom: 10px;
}

.benefits-container {
  width: 100%;
}

.benefit-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.add-benefit {
  display: flex;
  align-items: center;
  margin-top: 10px;
}
</style>
